import {
    Box,
    Grid,
    GridSize,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import DotLegalTableControlsSearchField from "./DotLegalTableControlsSearchField";
import useDotLegalTableWithControlsStyles from "./DotLegalTableWithControls.styles";
import { ITableHeader, Order } from "./DotLegalTableWithControls.types";

export type { ITableHeader, Order };

export interface SearchFieldGridSizes {
    xs: boolean | GridSize;
    sm: boolean | GridSize;
    md: boolean | GridSize;
    lg: boolean | GridSize;
    xl: boolean | GridSize;
}

export interface IDotLegalTableWithControlsProps<T> {
    headers: Array<ITableHeader<T>>;
    defaultOrderBy: keyof T;
    defaultOrder: Order;
    emptyText?: string;
    isLoading: boolean;
    data?: Array<T>;
    renderRow: (row: T, i: number) => JSX.Element;
    clickableRows?: boolean;
    hideRowsPerPage?: boolean;
    paginationLabelOf: string;
    getUserSpecificPageLength: () => number | undefined;
    labelRowsPerPage: string;
    labelSearch: string;
    noOptionsLabel: string;
    extraControls?: JSX.Element;
    searchFieldFullWidth?: boolean;
    searchFieldGridSizes?: SearchFieldGridSizes;
}

const rowsPerPageOptions = [10, 25, 50, 100];

const defaultSearchFieldGridSizes: SearchFieldGridSizes = { xs: 12, sm: 6, md: 4, lg: true, xl: true };

function matchesSearch<T>(row: T, searchString: string): boolean {
    const trimmed = searchString.trim();
    if (!trimmed) return true;

    const exactMatch = trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 1;
    const term = (exactMatch ? trimmed.slice(1, -1) : trimmed).toLowerCase();

    return Object.values(row as Record<string, unknown>).some((value) => {
        if (value === null || value === undefined) return false;
        const stringValue = String(value).toLowerCase();
        return exactMatch ? stringValue === term : stringValue.includes(term);
    });
}

function compareRows<T>(a: T, b: T, orderBy: keyof T, order: Order): number {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (!aValue && aValue !== 0) return 1;
    if (!bValue && bValue !== 0) return -1;

    const aComparable = typeof aValue === "string" ? aValue.toLowerCase() : aValue;
    const bComparable = typeof bValue === "string" ? bValue.toLowerCase() : bValue;

    if (aComparable === bComparable) return 0;
    const isAscendingGreater = aComparable! > bComparable!;
    return (order === "asc" ? isAscendingGreater : !isAscendingGreater) ? 1 : -1;
}

// Ported from @dotlegal/dotlegal-ui-components' DotLegalTableWithControls (+ its internal DotLegalTable),
// which hardcoded its search field's background color with no way to override it per-instance. This is
// scoped to exactly what the app's three usages (OrganizationTable, UserTable, Surveys admin page) need:
// client-side search/sort/pagination over an in-memory data array, all of them with hideRowsPerPage set.
function DotLegalTableWithControls<T>(props: IDotLegalTableWithControlsProps<T>) {
    const theme = useTheme();
    const classes = useDotLegalTableWithControlsStyles({ clickableRows: props.clickableRows, hideRowsPerPage: props.hideRowsPerPage });
    const isMdDown = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const isLgUp = useMediaQuery(theme.breakpoints.up("lg"), { noSsr: true });

    const [rowsPerPage, setRowsPerPage] = useState(props.getUserSpecificPageLength() ?? 10);
    const [searchString, setSearchString] = useState("");
    const [order, setOrder] = useState<Order>(props.defaultOrder);
    const [orderBy, setOrderBy] = useState<keyof T>(props.defaultOrderBy);
    const [page, setPage] = useState(1);

    useEffect(() => {
        setRowsPerPage(props.getUserSpecificPageLength() ?? 10);
        // eslint-disable-next-line
    }, [props.getUserSpecificPageLength()]);

    useEffect(() => {
        setPage(1);
    }, [searchString, rowsPerPage]);

    const visibleHeaders = isMdDown ? props.headers.filter((h) => h.showOnMobile || h.showOnMobile === undefined) : props.headers;

    const onSort = (header: ITableHeader<T>) => {
        if (!header.property) return;
        if (header.property === orderBy) {
            setOrder(order === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(header.property);
            setOrder("asc");
        }
    };

    const data = props.data ?? [];
    const filteredAndSorted = data.filter((row) => matchesSearch(row, searchString)).sort((a, b) => compareRows(a, b, orderBy, order));
    const noOfPages = Math.ceil(filteredAndSorted.length / rowsPerPage) || 1;
    const currentPage = Math.min(page, noOfPages);
    const pagedRows = filteredAndSorted.slice(rowsPerPage * (currentPage - 1), rowsPerPage * currentPage);

    const from = filteredAndSorted.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const to = Math.min(filteredAndSorted.length, currentPage * rowsPerPage);

    const searchField = (
        <DotLegalTableControlsSearchField
            labelSearch={props.labelSearch}
            value={searchString}
            onChange={setSearchString}
            useFullWidth={props.searchFieldFullWidth ?? !isLgUp}
        />
    );

    return (
        <>
            <Box className={classes.controlsRow}>
                {!props.hideRowsPerPage && (
                    <Select size="small" value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))} sx={{ width: 100 }}>
                        {rowsPerPageOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                )}

                {props.extraControls ? (
                    <Grid container spacing={1}>
                        {props.extraControls}
                        <Grid item {...(props.searchFieldGridSizes ?? defaultSearchFieldGridSizes)} sx={{ display: "flex", justifyContent: "flex-end" }}>
                            {searchField}
                        </Grid>
                    </Grid>
                ) : (
                    searchField
                )}
            </Box>

            {!props.isLoading && data.length === 0 ? (
                <Typography className={classes.emptyText}>{props.emptyText}</Typography>
            ) : (
                <Box>
                    <Table size="small" className={classes.table}>
                        <TableHead>
                            <TableRow>
                                {visibleHeaders.map((header, i) => (
                                    <TableCell key={i} width={header.width} align={header.align}>
                                        {!header.notSortable && header.property ? (
                                            <TableSortLabel direction={order} active={orderBy === header.property} onClick={() => onSort(header)}>
                                                {header.text}
                                            </TableSortLabel>
                                        ) : (
                                            <Box>{header.text}</Box>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.isLoading
                                ? Array.from({ length: 5 }).map((_, rowIndex) => (
                                      <TableRow key={rowIndex}>
                                          {visibleHeaders.map((_, columnIndex) => (
                                              <TableCell key={columnIndex}>
                                                  <Skeleton animation="pulse" />
                                              </TableCell>
                                          ))}
                                      </TableRow>
                                  ))
                                : pagedRows.map((row, i) => props.renderRow(row, i))}
                        </TableBody>
                    </Table>

                    {noOfPages > 1 && <Pagination count={noOfPages} page={currentPage} onChange={(_, newPage) => setPage(newPage)} />}
                    <Typography className={classes.paginationLabel}>
                        {from} - {to} {props.paginationLabelOf} {filteredAndSorted.length}
                    </Typography>
                </Box>
            )}
        </>
    );
}

export default DotLegalTableWithControls;
